import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { useSaveWorkItem, useSprints } from './api';
import {
  WORK_ITEM_PRIORITY_LABELS,
  WORK_ITEM_TYPE_LABELS,
} from './labels';
import type { WorkItem } from './types';

export function WorkItemDialog({
  projectId,
  sprintId,
  item,
  onClose,
}: {
  projectId: string;
  sprintId?: string;
  item?: WorkItem;
  onClose: () => void;
}) {
  const save = useSaveWorkItem();
  const { data: users = [] } = useUserOptions();
  const { data: sprints = [] } = useSprints(projectId);

  const [form, setForm] = useState({
    itemType: item?.itemType ?? 'TASK',
    title: item?.title ?? '',
    description: item?.description ?? '',
    priority: item?.priority ?? 'MEDIUM',
    severity: item?.severity ?? 3,
    assigneeId: item?.assigneeId ?? '',
    estimateHours: item?.estimateHours ?? '',
    storyPoints: item?.storyPoints ?? '',
    dueDate: item?.dueDate?.slice(0, 10) ?? '',
    sprintId: item?.sprintId ?? sprintId ?? '',
    component: item?.component ?? '',
    environment: item?.environment ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
  const isBug = form.itemType === 'BUG';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{item ? `Edit ${item.key}` : 'New work item'}</DialogTitle>
          <DialogDescription>
            An estimate is not required, but a column full of unestimated work shows as a question
            mark on the board rather than as capacity.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pe-1">
          <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select
                value={form.itemType}
                onChange={(e) => set({ itemType: e.target.value as never })}
              >
                {Object.entries(WORK_ITEM_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => set({ title: e.target.value })} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              rows={4}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onChange={(e) => set({ priority: e.target.value as never })}
              >
                {Object.entries(WORK_ITEM_PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            {isBug && (
              <div className="space-y-1.5">
                <Label>Severity (1–5)</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={form.severity}
                  onChange={(e) => set({ severity: Number(e.target.value) })}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Assignee</Label>
              <Select value={form.assigneeId} onChange={(e) => set({ assigneeId: e.target.value })}>
                <option value="">Unassigned</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Estimate (h)</Label>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={form.estimateHours}
                onChange={(e) => set({ estimateHours: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Points</Label>
              <Input
                type="number"
                min={0}
                value={form.storyPoints}
                onChange={(e) => set({ storyPoints: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Due</Label>
              <Input
                type="date"
                value={form.dueDate}
                onChange={(e) => set({ dueDate: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Sprint</Label>
              <Select value={form.sprintId} onChange={(e) => set({ sprintId: e.target.value })}>
                <option value="">Backlog</option>
                {sprints
                  .filter((sprint) => sprint.status !== 'COMPLETED')
                  .map((sprint) => (
                    <option key={sprint.id} value={sprint.id}>
                      {sprint.name}
                    </option>
                  ))}
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Component</Label>
              <Input value={form.component} onChange={(e) => set({ component: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Environment</Label>
              <Input
                value={form.environment}
                placeholder={isBug ? 'Where it happened' : ''}
                onChange={(e) => set({ environment: e.target.value })}
              />
            </div>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || form.title.trim().length < 3}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({
                  id: item?.id,
                  projectId,
                  itemType: form.itemType,
                  title: form.title,
                  description: form.description || undefined,
                  priority: form.priority,
                  severity: isBug ? form.severity : undefined,
                  assigneeId: form.assigneeId || undefined,
                  estimateHours: form.estimateHours ? Number(form.estimateHours) : undefined,
                  storyPoints: form.storyPoints ? Number(form.storyPoints) : undefined,
                  dueDate: form.dueDate || undefined,
                  sprintId: form.sprintId || undefined,
                  component: form.component || undefined,
                  environment: form.environment || undefined,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save the item.',
                );
              }
            }}
          >
            {save.isPending ? 'Saving…' : item ? 'Save changes' : 'Create item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
